import classNames from 'classnames';
import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Popover, Tooltip } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import { BaseViewportPlugin, useFramework, useScale, useStageUnit } from '@/framework';
import { useLocaleStore } from '@/i18n';
import { getSysType, SYSTEM_TYPE } from '@/utils/platform-kit';
import { parseToUnit } from '@/utils/unit-kit';
import { IExportPlugin, ISize, PluginType } from '@/version-types';
import { type ReferenceLines } from '../reference-lines';
import { type RulerBar } from '../ruler-bar';
import { Hierarchy, HIERARCHY_EVENTS } from './hierarchy';
import { StageToolbar } from './stage-toolbar';
import './plugin.less';

type PopoverType = 'scale' | 'reference-line' | 'hierarchy' | '';

const scaleList = [4, 3, 2, 1.75, 1.5, 1.25, 1, 0.75, 0.5, 0.25, 0.15];

const useGlobalWidget = (state: boolean) => {
    const framework = useFramework();
    const visibleRef = useRef(state);
    useEffect(() => {
        const handler = framework.events.onMouseDown(() => {
            const hoverId = framework.operate.getHoverId();
            if (hoverId) {
                return false;
            }
            return visibleRef.current;
        }, 999);
        return () => {
            framework.events.off(handler);
        };
    }, []);

    return visibleRef;
};

const defaultHierarchyPosition = { right: 276, bottom: 60 };

const StageToolbarView = React.memo(() => {
    const { $t } = useLocaleStore();
    const scale = useScale();
    const framework = useFramework();
    const unit = useStageUnit();
    const stageToolbarRef = useRef<HTMLDivElement>(null);

    const [canvasSize, setCanvasSize] = useState<ISize>(framework.stage.getSize());
    const canvasSizeText = `${parseToUnit(canvasSize.width, unit)} x ${parseToUnit(canvasSize.height, unit)}${unit}`;

    const stageToolbar = framework.getInstance(StageToolbar);

    const [referenceLineVisible, setReferenceLineVisible] = useState(stageToolbar.isReferenceLineVisible());
    const isCenter = stageToolbar.isCenter();

    const [hierarchyAvailability, setHierarchyAvailability] = useState(stageToolbar.getHierarchyAvailability());

    useEffect(() => {
        const handleStageResize = framework.events.onStageResize(() => {
            setCanvasSize(framework.stage.getSize());
        });
        const handleActiveStageChange = framework.events.onActiveStageChange(() => {
            setCanvasSize(framework.stage.getSize());
        });

        const referenceLineVisibleHandler = stageToolbar.onReferenceLineVisibleChange((visible) => {
            setReferenceLineVisible(visible);
        });

        const hierarchyToggleShowHandler = stageToolbar.onHierarchyToggleShowChange(() => {
            setOpenType((prev) => {
                return prev === 'hierarchy' ? '' : 'hierarchy';
            });
        });

        return () => {
            framework.events.off(handleStageResize);
            framework.events.off(handleActiveStageChange);
            referenceLineVisibleHandler();
            hierarchyToggleShowHandler();
        };
    }, []);

    // 工具节点
    const toolNode = stageToolbar.getNode();

    const hotKey = getSysType() === SYSTEM_TYPE.MAC ? '⇧ + R' : 'Shift + R';

    const tryGetRulerBar = useCallback((throwError = true) => {
        const rulerBar = framework.getInstance<RulerBar>('RulerBar');
        if (!rulerBar && throwError) {
            throw new Error('RulerBar is not found');
        }
        return rulerBar;
    }, []);

    const tryGetReferenceLine = useCallback(() => {
        const referenceLine = framework.getInstance<ReferenceLines>('ReferenceLines');
        if (!referenceLine) {
            throw new Error('ReferenceLines is not found');
        }
        return referenceLine;
    }, []);

    const [showRulerBar, setShowRulerBar] = useState(tryGetRulerBar(false)?.getShowRulerBar());

    // 监听标尺显示的变化
    useEffect(() => {
        return tryGetRulerBar(false)?.onShowRulerBarChanged((show) => {
            setShowRulerBar(show);
        });
    }, []);

    // 标尺菜单
    const rulerBarOperations = (
        <>
            {showRulerBar ? (
                <li
                    onClick={() => {
                        tryGetRulerBar()?.hideRulerBar();
                    }}
                >
                    {$t((v) => v.navigatorLayer.hideRulerBar)} {hotKey}
                </li>
            ) : (
                <li
                    onClick={() => {
                        tryGetRulerBar()?.showRulerBar();
                    }}
                >
                    {$t((v) => v.navigatorLayer.showRulerBar)} {hotKey}
                </li>
            )}
            <div className="divide-line" />
        </>
    );

    // 参考线菜单
    const referenceLineOperations = (
        <>
            <li
                onClick={() => {
                    tryGetReferenceLine().addHorizontalReferenceLine();
                }}
            >
                {$t((v) => v.navigatorLayer.horizontalReferenceLine)}
            </li>
            <li
                onClick={() => {
                    tryGetReferenceLine().addVerticalReferenceLine();
                }}
            >
                {$t((v) => v.navigatorLayer.verticalReferenceLine)}
            </li>
            <li
                onClick={() => {
                    tryGetReferenceLine().clearReferenceLines();
                }}
            >
                {$t((v) => v.navigatorLayer.clearReferenceLine)}
            </li>
        </>
    );

    const scaleListContent = (
        <div className="l-navigator__navigator-menu">
            <ul>
                {scaleList.map((v, i) => {
                    return (
                        <li
                            key={i}
                            onClick={() => {
                                framework.stage.setIsAutoScale(false);
                                framework.stage.setScale(v);
                            }}
                            className={v === scale ? 'active' : ''}
                        >
                            {~~(v * 100)}%
                        </li>
                    );
                })}
                <li
                    onClick={() => {
                        // 自适应
                        framework.stage.setIsAutoScale(true);
                    }}
                >
                    {$t((v) => v.navigatorLayer.adaptScale)}
                </li>
                <li
                    onClick={() => {
                        // 100%
                        framework.stage.setIsAutoScale(false);
                        framework.stage.setScale(1);
                    }}
                >
                    {$t((v) => v.navigatorLayer.realSize)}
                </li>
            </ul>
        </div>
    );

    const sizeGlobalWidget = useGlobalWidget(false);
    const lineGlobalWidget = useGlobalWidget(false);
    const [openType, setOpenType] = useState<PopoverType>('');
    const [visible, setVisible] = useState(stageToolbar.isVisible());
    const [scaleOpen, setScaleOpen] = useState(false);

    useEffect(() => {
        return stageToolbar.onVisibleChange((visible) => {
            setVisible(visible);
        });
    }, []);

    // 监听外部对图层管理器的开关
    useEffect(() => {
        const listener = framework.events.on(HIERARCHY_EVENTS.TOGGLE_AVAILABILITY, (flag: boolean) => {
            setHierarchyAvailability(flag);
            if (!flag && openType === 'hierarchy') setOpenType('');
        });

        return () => {
            framework.events.off(listener);
        };
    }, [openType]);

    // 海外版禁用缩放隐藏特性
    const disabledAutoZoomInAndOut = useMemo(() => {
        return !!window.isIntl;
    }, []);

    useEffect(() => {
        if (!openType) {
            setScaleOpen(false);
        }
        if (openType !== 'hierarchy') {
            stageToolbar.setHierarchyPanelPosition({});
        }
        stageToolbar.emitPanelVisibleChange(openType);
    }, [openType]);

    const handleHierarchyPositionChange = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        const rect = stageToolbarRef.current?.getBoundingClientRect();
        if (!rect) return;
        defaultHierarchyPosition.right = window.innerWidth - rect.right;
    };

    if (!visible) {
        return null;
    }

    return (
        <BaseViewportPlugin name="stage-toolbar" styles={{ overflow: 'initial' }}>
            <div
                className={classNames('c-stage-toolbar', isCenter ? 'c-stage-toolbar-center' : '')}
                onMouseDown={(e) => {
                    e.stopPropagation();
                    setOpenType('');
                }}
            >
                <div
                    ref={stageToolbarRef}
                    className={classNames(
                        'l-navigator no-preview',
                        disabledAutoZoomInAndOut ? 'disabledAutoZoomInAndOut' : '',
                    )}
                    onMouseLeave={() => {
                        if (!openType) setScaleOpen(false);
                    }}
                >
                    {/* 参考线 */}
                    <div
                        style={{ display: referenceLineVisible ? 'flex' : 'none', cursor: 'pointer' }}
                        className="l-navigator__reference-line"
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <Tooltip
                            placement="top"
                            title={$t((v) => v.navigatorLayer.referenceLinesAndRulers)}
                            trigger="hover"
                        >
                            <Popover
                                onOpenChange={(e) => {
                                    lineGlobalWidget.current = e;
                                    setOpenType(e ? 'reference-line' : '');
                                }}
                                content={
                                    <ul>
                                        {rulerBarOperations}
                                        {referenceLineOperations}
                                    </ul>
                                }
                                trigger="click"
                                getPopupContainer={(triggerNode) => {
                                    console.log('triggerNode', triggerNode.parentNode);
                                    return triggerNode.parentNode as HTMLElement;
                                }}
                                placement="top"
                                overlayClassName="reference-line-operation-menu"
                                open={openType === 'reference-line'}
                            >
                                <span>
                                    <IconFont name="ReferenceLineBold" fontSize={16} />
                                </span>
                            </Popover>
                        </Tooltip>
                    </div>

                    {/* 图层管理 */}
                    {hierarchyAvailability ? (
                        <div className="l-navigator__hierarchy" onMouseDown={handleHierarchyPositionChange}>
                            <Hierarchy.Trigger
                                open={openType === 'hierarchy'}
                                onOpenChange={(open) => {
                                    setOpenType(open ? 'hierarchy' : '');
                                }}
                            />

                            {openType === 'hierarchy' ? (
                                <Hierarchy.Panel
                                    position={{
                                        ...defaultHierarchyPosition,
                                        ...stageToolbar.getHierarchyPanelPosition(),
                                    }}
                                    onClose={() => {
                                        setOpenType('');
                                    }}
                                />
                            ) : null}
                        </div>
                    ) : null}

                    {/* 缩放按钮 */}
                    <div
                        className={classNames('l-navigator__scale', scaleOpen ? 'open' : '')}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseEnter={(e) => {
                            setScaleOpen(true);
                        }}
                        onClick={(e) => {
                            setTimeout(() => {
                                setScaleOpen(true);
                            }, 0);
                        }}
                    >
                        <Tooltip
                            placement="top"
                            title={
                                scale <= scaleList[scaleList.length - 1]
                                    ? $t((v) => v.navigatorLayer.minCanvas)
                                    : $t((v) => v.navigatorLayer.shrink)
                            }
                            trigger="hover"
                        >
                            <span
                                className="zoomInAndOut"
                                onClick={() => {
                                    setOpenType('');
                                    framework.stage.zoomOut();
                                }}
                            >
                                <IconFont name="MinusBlack" fontSize={13} />
                            </span>
                        </Tooltip>

                        <Tooltip
                            placement="top"
                            title={`${$t((v) => v.navigatorLayer.canvasSize)}：${canvasSizeText}`}
                            trigger="hover"
                        >
                            <Popover
                                onOpenChange={(e) => {
                                    sizeGlobalWidget.current = e;
                                    setOpenType(e ? 'scale' : '');
                                }}
                                content={scaleListContent}
                                trigger="click"
                                getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                                placement="topLeft"
                                overlayClassName="app-operate-scale-list"
                                open={openType === 'scale'}
                            >
                                <span className="scale-label">{`${Math.round(scale * 100)}%`}</span>
                            </Popover>
                        </Tooltip>

                        <Tooltip
                            placement="top"
                            title={
                                scale >= scaleList[0]
                                    ? $t((v) => v.navigatorLayer.maxCanvas)
                                    : $t((v) => v.navigatorLayer.enlarge)
                            }
                            trigger="hover"
                        >
                            <span
                                className="zoomInAndOut"
                                onClick={() => {
                                    setOpenType('');
                                    framework.stage.zoomIn();
                                }}
                            >
                                <IconFont name="PlusBlack" fontSize={13} />
                            </span>
                        </Tooltip>
                    </div>
                    {toolNode}
                </div>
            </div>
        </BaseViewportPlugin>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.VIEWPORT,
    sort: 1,
    name: 'stage-toolbar',
    createInstance({ identity }) {
        return [{ key: 'StageToolbar', value: new StageToolbar(identity) }];
    },
    render() {
        return <StageToolbarView />;
    },
};

export default exportPlugin;
